/**
 * Search autocomplete — fetches countries, cities, attractions, and tours
 * matching the query. Used by the search bar dropdown.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@mondivago/supabase';
import { buildTourSlug } from '@/shared/seo/seoRouting';
import type { AutocompleteResult } from '@mondivago/types';

export type { AutocompleteResult };

/* ── Supabase row shape interfaces ── */

interface CountryResult {
  name: string;
  slug: string;
  iso2?: string | null;
}

interface CityWithCountryResult {
  id: string;
  name: string;
  slug: string;
  photo_url: string | null;
  countries:
    | { name: string; slug: string; iso2: string | null }
    | { name: string; slug: string; iso2: string | null }[];
}

interface AttractionResult {
  id: string;
  name: string;
  slug: string;
  photo_url: string | null;
  cities:
    | {
        name: string;
        slug: string;
        countries:
          | { name: string; slug: string; iso2: string | null }
          | { name: string; slug: string; iso2: string | null }[];
      }
    | {
        name: string;
        slug: string;
        countries:
          | { name: string; slug: string; iso2: string | null }
          | { name: string; slug: string; iso2: string | null }[];
      }[];
}

interface TourSearchResult {
  id: string;
  short_id: string;
  title: string;
  photos: string[] | null;
  currency?: string | null;
  tour_minimum_prices: { min_price?: number | null }[] | { min_price?: number | null } | null;
  cities:
    | {
        name: string;
        slug: string;
        countries:
          | { name: string; slug: string; iso2: string | null }
          | { name: string; slug: string; iso2: string | null }[];
      }
    | {
        name: string;
        slug: string;
        countries:
          | { name: string; slug: string; iso2: string | null }
          | { name: string; slug: string; iso2: string | null }[];
      }[];
}

/** Extract the minimum price from a tour_minimum_prices join result. */
function resolveMinPrice(
  prices: { min_price?: number | null }[] | { min_price?: number | null } | null
): number | undefined {
  if (!prices) return undefined;
  if (Array.isArray(prices)) {
    const val = prices[0]?.min_price;
    return val ?? undefined;
  }
  return prices.min_price ?? undefined;
}

export function useAutocomplete(query: string) {
  const [results, setResults] = useState<AutocompleteResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchResults = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const lc = q.toLowerCase();

    try {
      const [countriesRes, citiesRes, attractionsRes, toursRes] = await Promise.all([
        supabase
          .from('content_countries')
          .select('name, slug, iso2')
          .ilike('name', `%${lc}%`)
          .eq('is_active', true)
          .limit(3),
        supabase
          .from('content_cities')
          .select('id, name, slug, photo_url, countries:content_countries(name, slug, iso2)')
          .ilike('name', `%${lc}%`)
          .limit(3),
        supabase
          .from('content_attractions')
          .select(
            'id, name, slug, photo_url, cities:content_cities!inner(name, slug, countries:content_countries!inner(name, slug, iso2))'
          )
          .ilike('name', `%${lc}%`)
          .eq('is_active', true)
          .limit(3),
        supabase
          .from('tours')
          .select(
            'id, short_id, title, photos, tour_minimum_prices(min_price), currency, cities:content_cities!inner(name, slug, countries:content_countries!inner(name, slug, iso2))'
          )
          .ilike('title', `%${lc}%`)
          .eq('status', 'published')
          .limit(3),
      ]);

      const autocompleteResults: AutocompleteResult[] = [];

      for (const c of (countriesRes.data as unknown as CountryResult[]) ?? []) {
        autocompleteResults.push({
          type: 'country',
          id: c.slug,
          name: c.name,
          slug: c.slug,
          iso2: c.iso2 ?? undefined,
        });
      }

      for (const city of (citiesRes.data as unknown as CityWithCountryResult[]) ?? []) {
        const country = Array.isArray(city.countries) ? city.countries[0] : city.countries;
        autocompleteResults.push({
          type: 'city',
          id: city.slug,
          name: city.name,
          slug: city.slug,
          imageUrl: city.photo_url ?? undefined,
          countrySlug: country?.slug,
          countryName: country?.name,
          iso2: country?.iso2 ?? undefined,
        });
      }

      for (const attr of (attractionsRes.data as unknown as AttractionResult[]) ?? []) {
        const cityData = Array.isArray(attr.cities) ? attr.cities[0] : attr.cities;
        const country = cityData
          ? Array.isArray(cityData.countries)
            ? cityData.countries[0]
            : cityData.countries
          : undefined;
        autocompleteResults.push({
          type: 'attraction',
          id: attr.slug,
          name: attr.name,
          slug: attr.slug,
          imageUrl: attr.photo_url ?? undefined,
          citySlug: cityData?.slug,
          countrySlug: country?.slug,
          cityName: cityData?.name,
          countryName: country?.name,
          iso2: country?.iso2 ?? undefined,
        });
      }

      for (const tData of (toursRes.data as unknown as TourSearchResult[]) ?? []) {
        const cityData = Array.isArray(tData.cities) ? tData.cities[0] : tData.cities;
        const country = cityData
          ? Array.isArray(cityData.countries)
            ? cityData.countries[0]
            : cityData.countries
          : undefined;
        autocompleteResults.push({
          type: 'tour',
          id: tData.short_id,
          name: tData.title,
          slug: buildTourSlug(tData.title, tData.short_id),
          imageUrl: tData.photos?.[0] ?? undefined,
          citySlug: cityData?.slug,
          countrySlug: country?.slug,
          cityName: cityData?.name,
          countryName: country?.name,
          iso2: country?.iso2 ?? undefined,
          startingPrice: resolveMinPrice(tData.tour_minimum_prices),
          currency: tData.currency ?? undefined,
        });
      }

      setResults(autocompleteResults.slice(0, 8));
    } catch {
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      void fetchResults(q);
    }, 300);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [query, fetchResults]);

  return { results, isLoading };
}
